import React, { useState, useEffect } from 'react';
import './RoomSelection.css';
import { apiService } from '../../services/api';
import { format, parseISO } from 'date-fns';
import ShimmerCard from './ShimmerCard';

const RoomSelection = ({ 
  selectedProperty, 
  onBack, 
  checkInDate, 
  checkOutDate,
  onShowFilters,
  filters,
  onFiltersChange,
  setFilterOptions,
  setFilteredRoomCount,
  onRoomSelect,
  guests,
  rawRoomsData,
  setRawRoomsData,
  processedRooms,
  setProcessedRooms
}) => {
  const [filteredRooms, setFilteredRooms] = useState([]); // Rooms after filtering and sorting
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [nights, setNights] = useState(0);

  const activeFilterCount =
    filters.rateTypes.length +
    filters.roomTypes.length +
    filters.bedTypes.length +
    (filters.accessibleOnly ? 1 : 0);
  
  const handleResetFilters = () => {
    onFiltersChange({
      sortBy: 'price_asc',
      rateTypes: [],
      roomTypes: [],
      bedTypes: [],
      accessibleOnly: false,
    });
  };

  useEffect(() => {
    const loadRooms = async () => {
      if (!selectedProperty) {
        console.log('No property selected, skipping room load');
        return;
      }

      try {
        console.log('Loading rooms for property:', selectedProperty);
        setLoading(true);
        setError(null);

        // Use provided dates or fallback to default
        const startDate = checkInDate || format(new Date(), 'yyyy-MM-dd');
        const endDate = checkOutDate || format(new Date(Date.now() + 24 * 60 * 60 * 1000), 'yyyy-MM-dd');

        console.log('Room availability request params:', {
          propertyId: selectedProperty.id,
          startDate,
          endDate
        });

        const data = await apiService.getRoomAvailability(
          selectedProperty.id,
          startDate,
          endDate,
          guests
        );

        console.log('Raw room availability data from API:', data);

        console.log('Raw room availability data:', data);
        setRawRoomsData(data);
        
        // Set nights from API response
        if (data && data.length > 0 && data[0].rate) {
          setNights(data[0].rate.nights_count || 0);
        } else {
          setNights(0);
        }

        // Extract filter options from raw data
        const allRateTypes = data.flatMap(r => {
          const rateNames = [r.rate?.name];
          if (r.packages) {
            r.packages.forEach(p => {
              if (p.rate?.name) {
                rateNames.push(p.rate.name);
              }
            });
          }
          return rateNames.filter(Boolean);
        });
        const rateTypes = [...new Set(allRateTypes)];
        
        const roomTypes = [...new Set(data.map(r => r.room?.name).filter(Boolean))];
        
        const allBedTypes = data.flatMap(r => 
          r.room?.specs?.filter(spec => spec.toLowerCase().includes('bed')) || []
        );
        const bedTypes = [...new Set(allBedTypes)];
        
        setFilterOptions({ rateTypes, roomTypes, bedTypes });
        
        const newProcessedRooms = data
          .filter(room => room && room.room && room.rate)
          .map(room => {
            const roomData = room.room;
            const rate = room.rate;
            
            setCurrentImageIndex(prev => ({
              ...prev,
              [roomData.id]: 0
            }));
            
            // Process images
            const images = [];
            
            // Add main image
            if (roomData.image) {
              images.push(roomData.image);
            }
            
            // Add carousel images
            if (roomData.carousel && Array.isArray(roomData.carousel)) {
              images.push(...roomData.carousel);
            }
            
            // Add additional images
            if (roomData.images && Array.isArray(roomData.images)) {
              images.push(...roomData.images);
            }
            
            // Remove duplicates and invalid entries
            const uniqueImages = [...new Set(images.filter(img => typeof img === 'string'))];
            
            return {
              id: roomData.id,
              name: roomData.name,
              description: roomData.short_description || roomData.detailed_description || 'Experience luxury and comfort in our beautifully appointed room.',
              images: uniqueImages,
              nightlyRate: rate.nightly_before_tax || rate.nightly_after_tax || 0,
              totalRate: rate.total_after_tax || rate.total_before_tax || 0,
              // Add raw data for filtering
              rateNames: [room.rate?.name, ...(room.packages?.map(p => p.rate?.name) || [])].filter(Boolean),
              type: roomData.name,
              bedTypes: roomData.specs?.filter(spec => spec.toLowerCase().includes('bed')) || [],
              bedQuantity: roomData.bed_quantity,
              accessible: roomData.ada || false,
            };
          })
          .sort((a, b) => a.nightlyRate - b.nightlyRate);

        setProcessedRooms(newProcessedRooms);
      } catch (err) {
        console.error('Error loading rooms:', err);
        setError(err.message || 'Failed to load rooms');
      } finally {
        setLoading(false);
      }
    };

    loadRooms();
  }, [selectedProperty, checkInDate, checkOutDate, guests, setFilterOptions, setRawRoomsData, setProcessedRooms]);

  // This effect runs whenever filters or the original room list changes
  useEffect(() => {
    let roomsToFilter = [...rawRoomsData];

    // Apply multi-select filters
    if (filters.rateTypes.length > 0) {
      roomsToFilter = roomsToFilter.filter(room => {
        const rateNames = [room.rate?.name, ...(room.packages?.map(p => p.rate?.name) || [])].filter(Boolean);
        return filters.rateTypes.some(selectedRate => rateNames.includes(selectedRate));
      });
    }
    if (filters.roomTypes.length > 0) {
      roomsToFilter = roomsToFilter.filter(room => filters.roomTypes.includes(room.room.name));
    }
    if (filters.bedTypes.length > 0) {
      roomsToFilter = roomsToFilter.filter(room => {
        const bedTypes = room.room.specs?.filter(spec => spec.toLowerCase().includes('bed')) || [];
        return filters.bedTypes.some(selectedBedType => bedTypes.includes(selectedBedType));
      });
    }

    // Apply accessibility filter
    if (filters.accessibleOnly) {
      // Assuming 'accessible' is a boolean property on the room
      roomsToFilter = roomsToFilter.filter(room => room.room.ada);
    }
    
    // Apply sorting
    roomsToFilter.sort((a, b) => {
      const aRate = a.rate?.nightly_before_tax || 0;
      const bRate = b.rate?.nightly_before_tax || 0;
      if (filters.sortBy === 'price_asc') {
        return aRate - bRate;
      } else {
        return bRate - aRate;
      }
    });

    setFilteredRooms(roomsToFilter);
    setFilteredRoomCount(roomsToFilter.length);

  }, [filters, rawRoomsData, setFilteredRoomCount]);

  const handleImageNavigation = (roomId, direction) => {
    setCurrentImageIndex(prev => {
      const room = rawRoomsData.find(r => r.room.id === roomId);
      if (!room) return prev;

      const currentIndex = prev[roomId] || 0;
      const totalImages = room.room.carousel?.length || 0;
      let newIndex;

      if (direction === 'next') {
        newIndex = (currentIndex + 1) % totalImages;
      } else {
        newIndex = (currentIndex - 1 + totalImages) % totalImages;
      }

      return {
        ...prev,
        [roomId]: newIndex
      };
    });
  };

  const formattedDateRange = () => {
    if (checkInDate && checkOutDate) {
      const start = parseISO(checkInDate);
      const end = parseISO(checkOutDate);
      const formatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
      const checkinStr = start.toLocaleDateString('en-US', formatOptions);
      const checkoutStr = end.toLocaleDateString('en-US', formatOptions);
      return `${checkinStr} - ${checkoutStr}`;
    }
    return '';
  };

  if (loading) {
    return (
      <div className="room-selection">
        <div className="ios-nav-bar">
          <button className="ios-nav-back" onClick={onBack}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <h1 className="ios-nav-title">{selectedProperty.name}</h1>
          <div className="ios-nav-right"></div>
        </div>
        <div className="room-list-loading">
          {[...Array(5)].map((_, index) => <ShimmerCard key={index} />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="room-selection error">
        <div className="ios-nav-bar">
          <button className="ios-nav-back" onClick={onBack}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <h1 className="ios-nav-title">Error</h1>
          <div className="ios-nav-right"></div>
        </div>
        <div className="error-content">
          <p>{error}</p>
        </div>
      </div>
    );
  }
  
  if (filteredRooms.length === 0 && !loading) {
    return (
      <div className="room-selection">
         <div className="ios-nav-bar">
          <button className="ios-nav-back" onClick={onBack}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <h1 className="ios-nav-title">{selectedProperty.name}</h1>
          <div className="ios-nav-right">
            <button className="filter-icon-button" onClick={onShowFilters} aria-label="Filters">
              <svg className="filter-icon" viewBox="0 0 24 24"><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/></svg>
              {activeFilterCount > 0 && <span className="filter-count-badge">{activeFilterCount}</span>}
            </button>
          </div>
        </div>
        <div className="sub-header-bar">
          <div className="date-guest-info">
            <span className="info-item">
              <svg className="icon" viewBox="0 0 24 24"><path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z"/></svg>
              <span>{checkInDate && checkOutDate ? `${format(parseISO(checkInDate), 'MMM d')} – ${format(parseISO(checkOutDate), 'MMM d')}` : ''}</span>
            </span>
            {nights > 0 && (
              <span className="info-item">
                <svg className="icon" viewBox="0 0 24 24"><path d="M21 10.78V8a3 3 0 0 0-3-3H6A3 3 0 0 0 3 8v2.78A2.25 2.25 0 0 0 2 13v6a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-1h14v1a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-6a2.25 2.25 0 0 0-1-2.22zM5 8a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v2H5zm14 7H5v-3h14z"/></svg>
                <span>{nights} night{nights > 1 ? 's' : ''}</span>
              </span>
            )}
            {guests && guests.adults > 0 && (
              <span className="info-item">
                <svg className="icon" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
                <span>{guests.adults} guest{guests.adults > 1 ? 's' : ''}</span>
              </span>
            )}
          </div>
        </div>
        <div className="no-rooms-content">
          <h2>No Rooms Available</h2>
          <p>There are no rooms matching your filters. Try adjusting your search criteria.</p>
          <button className="reset-filters-button" onClick={handleResetFilters}>
            Reset Filters
          </button>
        </div>
      </div>
    );
  }

  const renderRoomCard = (roomData) => {
    const { room, rate } = roomData;
    const images = room.carousel || [room.image];
    const currentIndex = currentImageIndex[room.id] || 0;

    return (
      <div className="room-card" key={room.id} onClick={() => onRoomSelect(roomData)}>
        <div className="room-image-container">
          <img 
            src={images[currentIndex]} 
            alt={room.name} 
            className="room-image"
          />
          {images.length > 1 && (
            <>
              <button className="image-nav prev" onClick={(e) => {e.stopPropagation(); handleImageNavigation(room.id, 'prev');}}>
                <svg viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6" /></svg>
              </button>
              <button className="image-nav next" onClick={(e) => {e.stopPropagation(); handleImageNavigation(room.id, 'next');}}>
                <svg viewBox="0 0 24 24"><path d="M9 18l6-6-6-6" /></svg>
              </button>
              <div className="image-counter">{currentIndex + 1} / {images.length}</div>
            </>
          )}
        </div>
        <div className="room-info">
          <h3 className="room-name">{room.name}</h3>
          <p className="room-description" dangerouslySetInnerHTML={{ __html: room.short_description }}></p>
          <div className="room-price">
            <span className="price-amount">${rate.nightly_before_tax}</span>
            <span className="price-label">Avg nightly, excl. fees</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="room-selection">
      <div className="ios-nav-bar">
        <button className="ios-nav-back" onClick={onBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h1 className="ios-nav-title">{selectedProperty.name}</h1>
        <div className="ios-nav-right">
          <button className="filter-icon-button" onClick={onShowFilters} aria-label="Filters">
            <svg className="filter-icon" viewBox="0 0 24 24"><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/></svg>
            {activeFilterCount > 0 && <span className="filter-count-badge">{activeFilterCount}</span>}
          </button>
        </div>
      </div>
      <div className="sub-header-bar">
        <div className="date-guest-info">
          <span className="info-item">
            <svg className="icon" viewBox="0 0 24 24"><path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z"/></svg>
            <span>{checkInDate && checkOutDate ? `${format(parseISO(checkInDate), 'MMM d')} – ${format(parseISO(checkOutDate), 'MMM d')}` : ''}</span>
          </span>
          {nights > 0 && (
            <span className="info-item">
              <svg className="icon" viewBox="0 0 24 24"><path d="M21 10.78V8a3 3 0 0 0-3-3H6A3 3 0 0 0 3 8v2.78A2.25 2.25 0 0 0 2 13v6a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-1h14v1a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-6a2.25 2.25 0 0 0-1-2.22zM5 8a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v2H5zm14 7H5v-3h14z"/></svg>
              <span>{nights} night{nights > 1 ? 's' : ''}</span>
            </span>
          )}
          {guests && guests.adults > 0 && (
            <span className="info-item">
              <svg className="icon" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
              <span>{guests.adults} guest{guests.adults > 1 ? 's' : ''}</span>
            </span>
          )}
        </div>
      </div>

      <div className="room-list">
        {filteredRooms.map(room => renderRoomCard(room))}
      </div>
    </div>
  );
};

export default RoomSelection;